import { computed, defineComponent, watch, ref, onMounted, } from '@vue/composition-api';
import { getSurveysList, getSurveyUsers, QuestionType, SURVEY_ANSWER_SEPARATOR, } from '@/api/v2/requests/surveys';
import { genericTableRowSize } from '@/components/Table/__new__/_components/TableCellGeneric';
import TableLoader from '@/views/_components/Table/TableLoader.vue';
import { useSurveyColumns } from '@/views/Brand/Surveys/_composables/use-survey-columns';
import { aggregateItemKey, aggregateItemValue, parseAggregateAnswer, } from '@/views/Brand/Surveys/_helpers/aggregate-export';
import { GenericAGTable } from '@/components/Table/AGTable/AGTable.vue';
import FloatingTextFilter from '@/components/Table/AGTable/_components/FloatingTextFilter.vue';
import FloatingSelectFilter from '@/components/Table/AGTable/_components/FloatingSelectFilter.vue';
import FloatingDatepickerFiler from '@/components/Table/AGTable/_components/FloatingDatepickerFilter.vue';
import TableFooter from '@/views/_components/Table/TableFooter.vue';
import { useTableQueryParams } from '@/components/Table/_composables/use-table-query-params';
import SurveyPreviewCell from './SurveyPreviewCell.vue';
import SurveyModal from '@/layouts/_components/Surveys/SurveyModal.vue';
import { useAuthUser } from '@/composables';
import { useClient } from '@/composables/use-client';
import { useBrand } from '@/composables/use-brand';
import { SystemRole } from '@/api/requests/users';
import { Icon, IconSize } from '@/components/Icon';
import { Popover } from '@/components/Popover';
import ShowingMenu from '@/components/EllipsisMenu/ShowingMenu.vue';
import { mdiDotsVertical } from '@mdi/js';
export default defineComponent({
    components: {
        TableFooter,
        TableLoader,
        Icon,
        Popover,
        ShowingMenu,
        // eslint-disable-next-line vue/no-unused-components
        FloatingTextFilter,
        // eslint-disable-next-line vue/no-unused-components
        FloatingSelectFilter,
        // eslint-disable-next-line vue/no-unused-components
        FloatingDatepickerFiler,
        // eslint-disable-next-line vue/no-unused-components
        SurveyPreviewCell,
        SurveyModal,
        AgTable: GenericAGTable(),
    },
    setup(props, ctx) {
        const params = ctx.root.$route.params;
        const isLoading = ref(false);
        const rows = ref([]);
        const totalCount = ref(0);
        const brandId = params.id;
        const agTableRef = ref(null);
        const showPreviewModal = ref(false);
        const selectedSurvey = ref(null);
        const { systemRole } = useAuthUser();
        const { client } = useClient();
        const { brand } = useBrand();
        const PREVIEW_ALLOWED_ROLES = [
            SystemRole.ADMIN,
            SystemRole.SUPER_ADMIN,
            SystemRole.EMPLOYEE,
            SystemRole.BRAND_ADMIN,
            SystemRole.STRUCTURE_MANAGER,
        ];
        const canPreview = computed(() => PREVIEW_ALLOWED_ROLES.includes(systemRole.value));
        const handlePreview = (survey) => {
            selectedSurvey.value = survey;
            showPreviewModal.value = true;
        };
        const { page, perPage, query: tableQuery, handleFilterChange: handleQueryFilterChange, handleSort, resetSort, } = useTableQueryParams(ctx, {
            defaultPerPage: 25,
            filterParams: {
                title: 'title',
                internalNumber: 'internalNumber',
                createdAt: 'createdAt',
            },
            fieldMap: {
                'user.place.internalNumber': 'placeNumber',
            },
            staticParams: {
                brandId,
            },
        });
        const query = computed(() => {
            return { ...tableQuery.value };
        });
        const fetchItems = async () => {
            isLoading.value = true;
            try {
                const { nodes, totalCount: nodesCount } = await getSurveysList(query.value ?? {});
                totalCount.value = nodesCount;
                rows.value = nodes;
            }
            catch (err) {
                ctx.root.$toast.error(ctx.root.$t(`brand-surveys.error-fetching-surveys`));
            }
            finally {
                isLoading.value = false;
            }
        };
        const changePage = (newPage) => {
            page.value = newPage;
        };
        const handleFilterChange = (filterModel) => {
            handleQueryFilterChange(filterModel);
        };
        const handleSortChange = (field, sort) => {
            handleSort(field, sort);
        };
        const handleResetSort = () => {
            resetSort();
        };
        const handlePerPageChange = (newPerPage) => {
            perPage.value = newPerPage;
            page.value = 1;
        };
        const closePreviewModal = () => {
            showPreviewModal.value = false;
            selectedSurvey.value = null;
        };
        const selectedSurveys = ref([]);
        const onSelectionChanged = (selectedRows) => {
            selectedSurveys.value = selectedRows;
        };
        const handleRowClick = (row) => {
            ctx.root.$router.push({
                name: 'SurveyUsers',
                params: { surveyId: row.id },
            });
        };
        const formatAnswer = (answer, question) => {
            if (!answer)
                return '';
            switch (question.type) {
                case QuestionType.CHECKBOX:
                    return answer
                        .split(SURVEY_ANSWER_SEPARATOR)
                        .filter(Boolean)
                        .join(', ');
                case QuestionType.FILE:
                    return answer
                        .split(SURVEY_ANSWER_SEPARATOR)
                        .filter(Boolean)
                        .join(', ');
                default:
                    return answer;
            }
        };
        const buildSheetColumns = (survey) => {
            const columns = [
                {
                    header: ctx.root.$t('brand-surveys.export.client'),
                    key: 'clientName',
                    width: 20,
                },
                {
                    header: ctx.root.$t('brand-surveys.export.brand'),
                    key: 'brandName',
                    width: 20,
                },
                {
                    header: ctx.root.$t('brand-surveys.export.user-id'),
                    key: 'userId',
                    width: 36,
                },
                {
                    header: ctx.root.$t('brand-surveys.details.place-number'),
                    key: 'placeNumber',
                    width: 15,
                },
                {
                    header: ctx.root.$t('brand-surveys.details.place-name'),
                    key: 'placeName',
                    width: 25,
                },
                {
                    header: ctx.root.$t('brand-surveys.details.full-name'),
                    key: 'fullName',
                    width: 25,
                },
            ];
            const questions = survey.questions || [];
            for (const [idx, question] of questions.entries()) {
                if (question.type === QuestionType.AGGREGATE) {
                    const items = question.items ?? [];
                    for (const [itemIdx, item] of items.entries()) {
                        columns.push({
                            header: `${question.name} - ${item.label}`,
                            key: aggregateItemKey(idx, itemIdx),
                            width: 30,
                        });
                    }
                }
                else {
                    columns.push({ header: question.name, key: `q${idx}`, width: 50 });
                }
                columns.push({
                    header: `${question.name} - ${ctx.root.$t('brand-surveys.export.file')}`,
                    key: `q${idx}_file`,
                    width: 50,
                }, {
                    header: `${question.name} - ${ctx.root.$t('brand-surveys.export.comment')}`,
                    key: `q${idx}_comment`,
                    width: 50,
                });
            }
            return columns;
        };
        const isExportingRef = ref(false);
        const saveProgressRef = ref({ parsed: 0, total: 1, percent: 0 });
        const mapNode = (node, survey) => {
            const result = {
                clientName: client.value?.name || '',
                brandName: brand.value?.name || '',
                userId: node.user?.id || '',
                placeNumber: node.user?.place?.number || '',
                placeName: node.user?.place?.name || '',
                fullName: node.user?.fullName || '',
            };
            const questions = survey.questions || [];
            for (const [idx, question] of questions.entries()) {
                const rawAnswer = node.answers?.[idx] || '';
                if (question.type === QuestionType.AGGREGATE) {
                    const parsed = parseAggregateAnswer(rawAnswer);
                    const items = question.items ?? [];
                    for (const [itemIdx, item] of items.entries()) {
                        result[aggregateItemKey(idx, itemIdx)] = aggregateItemValue(parsed[item.label], item.inputType);
                    }
                }
                else {
                    result[`q${idx}`] = formatAnswer(rawAnswer, question);
                }
                result[`q${idx}_file`] = node?.files?.[idx] ?? '';
                result[`q${idx}_comment`] = node?.comments?.[idx] ?? '';
            }
            return result;
        };
        const sanitizeSheetName = (name) => {
            // Excel sheet name max 31 chars, no special chars: \ / * ? : [ ]
            // eslint-disable-next-line no-useless-escape
            return name.replace(/[\\/*?:\[\]]/g, '').substring(0, 31) || 'Sheet';
        };
        const exportSurveys = async (surveys) => {
            if (surveys.length === 0)
                return;
            isExportingRef.value = true;
            try {
                const ExcelJS = await import('exceljs');
                const workbook = new ExcelJS.Workbook();
                // First pass: count total users across all surveys
                let totalUsers = 0;
                const surveyCounts = [];
                for (const survey of surveys) {
                    const { totalCount } = await getSurveyUsers(survey.id, {
                        page: 1,
                        perPage: 1,
                        isAnswered: 'true',
                    });
                    totalUsers += totalCount;
                    surveyCounts.push({ survey, total: totalCount });
                }
                let parsed = 0;
                saveProgressRef.value = { parsed: 0, total: totalUsers, percent: 0 };
                // Second pass: each survey gets its own sheet
                const usedNames = new Set();
                for (const { survey, total } of surveyCounts) {
                    let sheetName = sanitizeSheetName(survey.title);
                    if (usedNames.has(sheetName)) {
                        sheetName = sanitizeSheetName(`${survey.internalNumber} ${survey.title}`);
                    }
                    usedNames.add(sheetName);
                    const sheet = workbook.addWorksheet(sheetName);
                    sheet.columns = buildSheetColumns(survey);
                    const perPage = 500;
                    const pages = Math.ceil(total / perPage);
                    for (let page = 1; page <= pages; page++) {
                        const result = await getSurveyUsers(survey.id, {
                            page,
                            perPage,
                            sort: '-createdAt',
                            isAnswered: 'true',
                        });
                        for (const node of result.nodes) {
                            sheet.addRow(mapNode(node, survey));
                        }
                        parsed += result.nodes.length;
                        const percent = totalUsers > 0 ? Math.floor((parsed / totalUsers) * 100) : 0;
                        saveProgressRef.value = { parsed, total: totalUsers, percent };
                    }
                }
                // Save file
                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `surveys-export-${Date.now()}.xlsx`;
                document.body.append(a);
                a.click();
                URL.revokeObjectURL(url);
                a.remove();
            }
            finally {
                saveProgressRef.value = { parsed: 0, total: 1, percent: 0 };
                isExportingRef.value = false;
            }
        };
        const handleExportAll = async () => {
            try {
                let allSurveys = [];
                let page = 1;
                let totalCount = 0;
                do {
                    const result = await getSurveysList({
                        ...query.value,
                        brandId,
                        page,
                        perPage: 500,
                    });
                    totalCount = result.totalCount;
                    allSurveys = allSurveys.concat(result.nodes);
                    page += 1;
                } while (allSurveys.length < totalCount);
                await exportSurveys(allSurveys);
            }
            catch (err) {
                ctx.root.$toast.error(ctx.root.$t('brand-surveys.export.error'));
            }
        };
        const handleExportSelected = async () => {
            if (selectedSurveys.value.length === 0)
                return;
            try {
                await exportSurveys(selectedSurveys.value);
            }
            catch (err) {
                ctx.root.$toast.error(ctx.root.$t('brand-surveys.export.error'));
            }
        };
        const menuActions = computed(() => ({
            exportAll: {
                name: ctx.root.$t('brand-surveys.export.export-all'),
                handler: handleExportAll,
            },
            exportSelected: {
                name: ctx.root.$t('brand-surveys.export.export-selected', {
                    count: selectedSurveys.value.length,
                }),
                handler: handleExportSelected,
                disabled: selectedSurveys.value.length === 0,
            },
        }));
        onMounted(async () => {
            await fetchItems();
        });
        const { columns } = useSurveyColumns(ctx, {
            onPreview: handlePreview,
            canPreview: canPreview.value,
        });
        watch(query, () => {
            fetchItems();
        }, { deep: true });
        return {
            columns,
            tableQuery,
            page,
            perPage,
            totalCount,
            genericTableRowSize,
            rows,
            isLoading,
            handleFilterChange,
            handleSortChange,
            handleResetSort,
            changePage,
            handlePerPageChange,
            agTableRef,
            showPreviewModal,
            selectedSurvey,
            closePreviewModal,
            handleRowClick,
            onSelectionChanged,
            selectedSurveys,
            menuActions,
            handleExportAll,
            handleExportSelected,
            isExporting: isExportingRef,
            saveProgress: saveProgressRef,
            mdiDotsVertical,
            IconSize,
        };
    },
});
